import {
  HubConnectionBuilder,
  HubConnectionState,
  type HubConnection,
  type IHttpConnectionOptions,
  type IRetryPolicy,
} from "@microsoft/signalr";

import type { RealtimeEngineOptions, RealtimeFrame } from "./types";

const RECONNECT_DELAYS_MS = [0, 1000, 2000, 5000, 10000];
const MAX_RECONNECT_DELAY_MS = 30000;

// Reconnect forever. A deploy or slot swap drops every client at once, and a fixed retry list would
// leave them permanently disconnected until the page is reloaded.
const reconnectPolicy: IRetryPolicy = {
  nextRetryDelayInMilliseconds: ({ previousRetryCount }) =>
    RECONNECT_DELAYS_MS[previousRetryCount] ?? MAX_RECONNECT_DELAY_MS,
};

const BROADCAST_METHOD = "broadcast";

export class RealtimeConnection {
  private connection?: HubConnection;
  private startPromise?: Promise<void>;
  private reconnectWaiters: Array<() => void> = [];

  constructor(
    private readonly options: RealtimeEngineOptions,
    private readonly onFrame: (frame: RealtimeFrame) => void,
    private readonly onReconnected: () => void,
    private readonly onPermanentClose?: () => void,
  ) {}

  async join(topic: string): Promise<string> {
    await this.ensureStarted();
    return this.connection!.invoke<string>("JoinChannel", topic);
  }

  async leave(handle: string): Promise<void> {
    if (this.connection?.state !== HubConnectionState.Connected) return;
    await this.connection.invoke("LeaveChannel", handle);
  }

  async stop(): Promise<void> {
    await this.connection?.stop();
    this.connection = undefined;
    this.startPromise = undefined;
  }

  private build(): HubConnection {
    const connectionOptions: IHttpConnectionOptions = {
      accessTokenFactory: () => this.options.accessToken(),
    };

    const connection = new HubConnectionBuilder()
      .withUrl(`${this.options.url}${this.options.hubPath}`, connectionOptions)
      .withAutomaticReconnect(reconnectPolicy)
      .build();

    connection.on(BROADCAST_METHOD, (frame: RealtimeFrame) => this.onFrame(frame));
    connection.onreconnected(() => {
      this.releaseReconnectWaiters();
      this.onReconnected();
    });
    connection.onclose(() => {
      this.releaseReconnectWaiters();
      this.onPermanentClose?.();
    });

    return connection;
  }

  private async ensureStarted(): Promise<void> {
    if (this.connection?.state === HubConnectionState.Connected) return;
    if (this.startPromise) return this.startPromise;

    if (this.connection?.state === HubConnectionState.Reconnecting) {
      await new Promise<void>((resolve) => this.reconnectWaiters.push(resolve));
      return;
    }

    this.connection ??= this.build();
    this.startPromise = this.connection.start();

    try {
      await this.startPromise;
    } finally {
      this.startPromise = undefined;
    }
  }

  private releaseReconnectWaiters(): void {
    const waiters = this.reconnectWaiters;
    this.reconnectWaiters = [];
    for (const resolve of waiters) resolve();
  }
}
